<template>
  <div>
    <div class="d-flex align-center mb-2">
      <v-chip
        :color="isAllConfirmed ? 'success' : 'warning'"
        small
        label
        class="mr-2"
      >
        {{ isAllConfirmed ? '確定済み' : '下書き' }}
      </v-chip>

      <v-spacer />

      <v-btn
        v-if="!isAllConfirmed"
        color="primary"
        :loading="isConfirming"
        :disabled="isConfirming"
        @click="openConfirmDialog"
      >
        最終確定
      </v-btn>

      <v-btn
        v-else
        color="error"
        outlined
        :loading="isUnconfirming"
        :disabled="isUnconfirming"
        @click="openUnconfirmDialog"
      >
        確定解除
      </v-btn>
    </div>

    <v-simple-table class="confirmation-table">
      <template #default>
        <thead>
          <tr>
            <th class="text-left user-col">
              ユーザー
            </th>
            <th
              v-for="column in columns"
              :key="column.key"
              class="text-right"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="conf in confirmations"
            :key="conf.id"
          >
            <td class="user-col">
              <a
                class="user-name-link"
                @click="onSelectUser(conf)"
              >
                {{ conf.attributes.userName }}
              </a>
            </td>
            <td
              v-for="column in columns"
              :key="column.key"
              class="text-right"
            >
              <template v-if="cellOf(conf, column.key)">
                <v-text-field
                  :value="cellOf(conf, column.key).attributes.value"
                  type="number"
                  dense
                  hide-details
                  reverse
                  :readonly="isCellReadonly(conf, cellOf(conf, column.key))"
                  :loading="isCellUpdating(conf, column.key)"
                  :class="cellClass(conf, column.key)"
                  class="cell-input"
                  @change="onCellChange(conf, column.key, $event)"
                />
              </template>
              <span v-else>—</span>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-dialog
      v-model="confirmDialog"
      max-width="420"
    >
      <v-card>
        <v-card-title class="text-h6">
          最終確定の確認
        </v-card-title>
        <v-card-text>
          選択中の確定データを最終確定します。確定後は編集できなくなります。よろしいですか？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="confirmDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :loading="isConfirming"
            @click="onConfirm"
          >
            最終確定する
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="unconfirmDialog"
      max-width="420"
    >
      <v-card>
        <v-card-title class="text-h6">
          確定解除の確認
        </v-card-title>
        <v-card-text>
          確定を解除して下書きに戻します。再び編集できるようになります（値は保持されます）。よろしいですか？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="unconfirmDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            :loading="isUnconfirming"
            @click="onUnconfirm"
          >
            確定解除する
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'

export default {
  props: {
    confirmations: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    cellOf: {
      type: Function,
      required: true,
    },
    updatingCellKeys: {
      type: Object,
      default: () => ({}),
    },
    isAllConfirmed: {
      type: Boolean,
      default: false,
    },
    isConfirming: {
      type: Boolean,
      default: false,
    },
    isUnconfirming: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const confirmDialog = ref(false)
    const unconfirmDialog = ref(false)

    const isReadonlyCellType = cell => {
      const cellType = cell && cell.attributes ? cell.attributes.cellType : null

      return cellType === 'computed' || cellType === 'withholding'
    }

    const isCellReadonly = (conf, cell) => conf.attributes.status === 'confirmed' || isReadonlyCellType(cell)

    const isCellUpdating = (conf, key) => !!props.updatingCellKeys[`${conf.id}:${key}`]

    const cellClass = (conf, key) => {
      const cell = props.cellOf(conf, key)
      if (!cell) return ''
      if (cell.attributes.manuallyOverridden) return 'cell-overridden'
      if (isReadonlyCellType(cell)) return 'cell-computed'

      return ''
    }

    const onCellChange = (conf, key, value) => {
      const cell = props.cellOf(conf, key)
      if (!cell) return
      if (isCellReadonly(conf, cell)) return
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return
      if (numeric === cell.attributes.value) return

      emit('update-cell', { conf, cellKey: key, value: numeric })
    }

    const openConfirmDialog = () => {
      confirmDialog.value = true
    }
    const openUnconfirmDialog = () => {
      unconfirmDialog.value = true
    }

    const onConfirm = () => {
      emit('confirm')
      confirmDialog.value = false
    }
    const onUnconfirm = () => {
      emit('unconfirm')
      unconfirmDialog.value = false
    }

    const onSelectUser = conf => {
      emit('select-user', conf)
    }

    return {
      confirmDialog,
      unconfirmDialog,
      isCellReadonly,
      isCellUpdating,
      cellClass,
      onCellChange,
      openConfirmDialog,
      openUnconfirmDialog,
      onConfirm,
      onUnconfirm,
      onSelectUser,
    }
  },
}
</script>

<style scoped>
.confirmation-table {
  overflow-x: auto;
}
.user-col {
  position: sticky;
  left: 0;
  /* sticky 列はスクロール時に透けないよう不透明背景にし、
     横スクロールしてくる他列より前面に出すため z-index を付与する */
  background: #fff;
  white-space: nowrap;
  z-index: 2;
}
.theme--dark .user-col {
  background: #3a3552;
}
/* ヘッダーの固定セルはボディの固定セルより前面（交差セル相当） */
thead .user-col {
  z-index: 3;
}
.user-name-link {
  color: var(--v-primary-base, #1976d2);
  cursor: pointer;
  text-decoration: underline;
}
.cell-input {
  min-width: 96px;
  padding-top: 0;
  margin-top: 0;
}
::v-deep .cell-overridden input {
  color: #e64a19 !important;
  font-weight: 700;
}
.cell-computed {
  background: rgba(0, 0, 0, 0.04);
}
.theme--dark .cell-computed {
  background: rgba(231, 227, 252, 0.08);
}
::v-deep .cell-computed input {
  color: rgba(0, 0, 0, 0.6) !important;
}
.theme--dark .cell-computed ::v-deep input {
  color: rgba(231, 227, 252, 0.7) !important;
}
</style>
